/**
 * undercover® keuzehulp-quiz (<uc-quiz>).
 * Stappen = fieldsets met radio's (native toetsenbordgedrag: pijltjes kiezen, Tab naar "volgende").
 * Klik/tik op een kaart gaat automatisch door; met het toetsenbord bevestig je met "volgende".
 * Resultaat: hoogste score (bij gelijkspel het bovenste resultaat-block), plus alternatief.
 * Slaat het resultaat op in sessionStorage('uc_quiz_result') en meet quiz_completed.
 */
(() => {
  if (customElements.get('uc-quiz')) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fill = (str, vars) => str.replace(/__(\w+?)__/g, (_, k) => (k in vars ? vars[k] : ''));

  const parseScores = (text) => {
    const out = {};
    (text || '').split(',').forEach((pair) => {
      const [key, value] = pair.split(':').map((s) => s && s.trim().toLowerCase());
      if (key && value !== undefined && !Number.isNaN(Number(value))) out[key] = Number(value);
    });
    return out;
  };

  const parseFlags = (text) =>
    (text || '')
      .split(',')
      .map((f) => f.trim().toLowerCase())
      .filter((f) => f && !f.includes(':'));

  const whenCartReady = () =>
    window.ucCart
      ? Promise.resolve(window.ucCart)
      : new Promise((resolve) => document.addEventListener('uc:cart-ready', () => resolve(window.ucCart), { once: true }));

  class UcQuiz extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.picks = [...this.querySelectorAll('[data-pick]')];
      this.resultEl = this.querySelector('[data-result]');
      this.backBtn = this.querySelector('[data-back]');
      this.countEl = this.querySelector('[data-count]');
      this.progress = this.querySelector('[data-progress]');
      this.live = this.querySelector('[data-live]');
      try {
        this.i18n = JSON.parse(this.querySelector('[data-i18n]')?.textContent || '{}');
      } catch (e) {
        this.i18n = {};
      }
      this.index = 0;
      this.lastPointer = 0;

      this.addEventListener('pointerdown', (e) => {
        this.lastPointer = e.target.closest('.uc-quiz__option') ? Date.now() : 0;
      });
      this.addEventListener('keydown', () => (this.lastPointer = 0));
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('click', (e) => this.onClick(e));
      this.querySelector('[data-form]')?.addEventListener('submit', (e) => e.preventDefault());

      this.onBlockSelect = (e) => this.editorSelect(e);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.picks.forEach((pick) => this.syncVariant(pick));
      this.render(false);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.advanceTimer);
    }

    get total() {
      return this.steps.length;
    }

    onChange(event) {
      const input = event.target;
      if (input.type !== 'radio') return;

      const step = input.closest('[data-step]');
      if (step) {
        const next = step.querySelector('[data-next]');
        if (next) next.disabled = false;
        // Muis/touch: automatisch door. Toetsenbord (pijltjes) niet, anders kun je niet rustig kiezen.
        if (Date.now() - this.lastPointer < 800) {
          clearTimeout(this.advanceTimer);
          this.advanceTimer = setTimeout(() => this.next(), reduceMotion() ? 0 : 320);
        }
        return;
      }

      const pick = input.closest('[data-pick]');
      if (pick) this.syncVariant(pick);
    }

    onClick(event) {
      const target = event.target.closest('button, a');
      if (!target || !this.contains(target)) return;
      if (target.matches('[data-next]')) this.next();
      else if (target.matches('[data-back]')) this.back();
      else if (target.matches('[data-restart]')) this.restart();
      else if (target.matches('[data-add]')) this.addToCart(target.closest('[data-pick]'), target);
    }

    next() {
      clearTimeout(this.advanceTimer);
      const current = this.steps[this.index];
      if (!current?.querySelector('input:checked')) return;
      if (this.index < this.total - 1) {
        this.index += 1;
        this.render(true, 'forward');
      } else {
        this.finish();
      }
    }

    back() {
      clearTimeout(this.advanceTimer);
      if (this.index === this.total) {
        this.index = this.total - 1;
      } else if (this.index > 0) {
        this.index -= 1;
      } else return;
      this.render(true, 'back');
    }

    restart() {
      this.querySelectorAll('[data-step] input:checked').forEach((input) => (input.checked = false));
      this.querySelectorAll('[data-next]').forEach((btn) => (btn.disabled = true));
      this.index = 0;
      this.render(true, 'back');
    }

    /* Toont stap this.index (of het resultaat als index === total) */
    render(focus, direction = 'forward') {
      const onResult = this.index >= this.total;

      this.steps.forEach((step, i) => {
        const active = i === this.index;
        step.hidden = !active;
        step.classList.remove('is-entering', 'is-back');
        if (active && focus && !reduceMotion()) {
          void step.offsetWidth;
          step.classList.add('is-entering');
          if (direction === 'back') step.classList.add('is-back');
        }
      });
      this.resultEl.hidden = !onResult;
      this.backBtn.hidden = this.index === 0;

      const answered = onResult ? this.total : this.index;
      this.progress.setAttribute('aria-valuenow', String(answered));
      this.progress.querySelector('span').style.setProperty('--p', this.total ? answered / this.total : 0);

      if (onResult) {
        this.countEl.textContent = this.i18n.result || '';
        return;
      }

      const current = this.steps[this.index];
      const vars = { current: this.index + 1, total: this.total };
      this.countEl.textContent = fill(this.i18n.stepOf || '', vars);
      if (current?.querySelector('input:checked')) current.querySelector('[data-next]').disabled = false;

      if (focus) {
        const question = current?.querySelector('legend');
        this.announce(fill(this.i18n.stepAnnounce || '', { ...vars, question: question?.textContent.trim() || '' }));
        question?.focus({ preventScroll: true });
        this.keepInView();
      }
    }

    keepInView() {
      const rect = this.querySelector('.uc-quiz__panel').getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
        this.querySelector('.uc-quiz__panel').scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    announce(text) {
      this.live.textContent = '';
      requestAnimationFrame(() => (this.live.textContent = text));
    }

    answers() {
      return this.steps
        .map((step) => step.querySelector('input:checked'))
        .filter(Boolean)
        .map((input) => ({
          label: input.dataset.label,
          scores: parseScores(input.dataset.scores),
          flags: parseFlags(input.dataset.flags),
        }));
    }

    finish() {
      const answers = this.answers();
      const flags = new Set(answers.flatMap((a) => a.flags));
      const kind = flags.has('kind');
      const cadeau = flags.has('cadeau');

      const totals = new Map();
      this.picks.forEach((pick) => totals.set(pick, 0));
      answers.forEach(({ scores }) => {
        this.picks.forEach((pick) => totals.set(pick, totals.get(pick) + (scores[pick.dataset.key] || 0)));
      });

      // Kinderen: alleen producten met een S/M-maat
      let ranked = this.picks.filter((pick) => pick.dataset.handle && (!kind || pick.dataset.kid === 'true'));
      if (!ranked.length) ranked = this.picks.filter((pick) => pick.dataset.handle);
      ranked = ranked
        .map((pick, order) => ({ pick, order, score: totals.get(pick) }))
        .sort((a, b) => b.score - a.score || a.order - b.order)
        .map((r) => r.pick);

      const top = ranked[0];
      const alt = ranked[1];
      if (!top) return;

      this.showResult(top, alt, { kind, cadeau });
      this.index = this.total;
      this.render(false);
      if (!reduceMotion()) {
        this.resultEl.classList.remove('is-entering');
        void this.resultEl.offsetWidth;
        this.resultEl.classList.add('is-entering');
      }

      const title = top.querySelector('[data-pick-title]');
      this.announce(fill(this.i18n.resultAnnounce || '', { title: top.dataset.title || '' }));
      title?.focus({ preventScroll: true });
      this.keepInView();

      const variant = this.currentVariant(top);
      const record = {
        result: top.dataset.key,
        product: top.dataset.handle,
        product_id: Number(top.dataset.productId) || null,
        variant_id: variant?.id || null,
        size: kind ? 'S/M' : top.dataset.kid === 'true' ? 'M/L' : null,
        alternative: alt?.dataset.key || null,
        alternative_product: alt?.dataset.handle || null,
        kind,
        cadeau,
        answers: answers.map((a) => a.label),
        ts: Date.now(),
      };
      try {
        sessionStorage.setItem('uc_quiz_result', JSON.stringify(record));
      } catch (e) {
        /* privémodus of opslag vol: de quiz werkt gewoon door */
      }
      window.ucTrack?.('quiz_completed', {
        result: record.result,
        alternative: record.alternative,
        answers: record.answers,
      });
      this.dispatchEvent(new CustomEvent('uc:quiz-completed', { bubbles: true, detail: record }));
    }

    showResult(top, alt, { kind, cadeau }) {
      this.picks.forEach((pick) => (pick.hidden = pick !== top));
      this.querySelectorAll('[data-alt]').forEach((el) => (el.hidden = !alt || el.dataset.alt !== alt.dataset.key));
      const badge = this.querySelector('[data-badge]');
      if (badge) badge.hidden = !cadeau;

      const reasonKey = kind ? 'kind' : cadeau ? 'cadeau' : 'default';
      top.querySelectorAll('[data-reason]').forEach((p) => (p.hidden = p.dataset.reason !== reasonKey));
      const giftNote = top.querySelector('[data-gift-note]');
      if (giftNote) giftNote.hidden = !cadeau;

      // Maat voorselecteren: kind → S/M, anders M/L. One size heeft geen maatkeuze.
      const sizeSet = top.querySelector('[data-kind="size"]');
      const hint = top.querySelector('[data-size-hint]');
      if (sizeSet) {
        const want = kind ? 'S/M' : 'M/L';
        const inputs = [...sizeSet.querySelectorAll('input')];
        const match = inputs.find((i) => i.value.toUpperCase().includes(want));
        if (match) match.checked = true;
        if (hint) {
          sizeSet.after(hint);
          hint.textContent = kind ? this.i18n.kid : this.i18n.adult;
          hint.hidden = !match;
        }
      } else if (hint) {
        hint.hidden = true;
      }

      // Kleur: eerste beschikbare combinatie met de gekozen maat
      const colorSet = top.querySelector('[data-kind="color"]');
      if (colorSet) {
        const variants = this.variants(top);
        const pos = Number(colorSet.dataset.option);
        const sizePos = sizeSet ? Number(sizeSet.dataset.option) : null;
        const size = sizeSet?.querySelector('input:checked')?.value;
        const firstOk = variants.find((v) => v.available && (!sizePos || v[`option${sizePos}`] === size));
        if (firstOk) {
          const input = [...colorSet.querySelectorAll('input')].find((i) => i.value === firstOk[`option${pos}`]);
          if (input) input.checked = true;
        }
      }
      this.syncVariant(top);
    }

    variants(pick) {
      if (!pick._variants) {
        try {
          pick._variants = JSON.parse(pick.querySelector('[data-variants]')?.textContent || '[]');
        } catch (e) {
          pick._variants = [];
        }
      }
      return pick._variants;
    }

    currentVariant(pick) {
      const variants = this.variants(pick);
      if (variants.length <= 1) return variants[0];
      const chosen = {};
      pick.querySelectorAll('[data-option]').forEach((set) => {
        const value = set.querySelector('input:checked')?.value;
        if (value) chosen[`option${set.dataset.option}`] = value;
      });
      return (
        variants.find((v) => Object.entries(chosen).every(([k, val]) => v[k] === val)) ||
        variants.find((v) => v.available) ||
        variants[0]
      );
    }

    syncVariant(pick) {
      if (!pick) return;
      const variant = this.currentVariant(pick);
      const button = pick.querySelector('[data-add]');
      if (!button) return;
      const label = button.querySelector('[data-add-label]');
      label.dataset.default ??= label.textContent;
      const ok = Boolean(variant?.available);
      button.disabled = !ok;
      label.textContent = ok ? label.dataset.default : this.i18n.unavailable;
      button.dataset.variantId = variant?.id || '';
      this.swapImage(pick, variant);
      this.tintCard(pick);
    }

    /* Kaartkleur volgt de gekozen kleur (zelfde logica als de productkaart) */
    tintCard(pick) {
      const media = pick.querySelector('.uc-quiz__pick-media');
      const color = pick.querySelector('[data-kind="color"] input:checked')?.value.toLowerCase();
      if (!media || !color) return;
      const map = [
        [/zwart|black/, 'var(--c-salie)'],
        [/groen|green/, 'var(--c-olijf-licht)'],
        [/blauw|blue/, 'color-mix(in srgb, var(--c-linnen) 70%, var(--c-salie))'],
        [/zand|sand/, 'var(--c-zand)'],
      ];
      const hit = map.find(([re]) => re.test(color));
      if (hit) media.style.setProperty('--card-bg', hit[1]);
    }

    /* Kleur gekozen → variantfoto (zachte crossfade) */
    swapImage(pick, variant) {
      const src = variant?.featured_image?.src;
      const img = pick.querySelector('.uc-quiz__pick-media img');
      if (!src || !img || img.dataset.variantSrc === src) return;
      const first = !img.dataset.variantSrc;
      img.dataset.variantSrc = src;
      const url = (w) => `${src}${src.includes('?') ? '&' : '?'}width=${w}`;
      const apply = () => {
        img.srcset = [480, 720, 960, 1200].map((w) => `${url(w)} ${w}w`).join(', ');
        img.src = url(960);
      };
      if (first || reduceMotion()) return apply();
      img.classList.add('is-swapping');
      setTimeout(() => {
        apply();
        img.addEventListener('load', () => img.classList.remove('is-swapping'), { once: true });
        setTimeout(() => img.classList.remove('is-swapping'), 600);
      }, 180);
    }

    async addToCart(pick, button) {
      const id = button.dataset.variantId;
      if (!id || button.getAttribute('aria-busy') === 'true') return;
      const label = button.querySelector('[data-add-label]');
      button.setAttribute('aria-busy', 'true');
      label.textContent = this.i18n.adding;
      try {
        const cart = await whenCartReady();
        await cart.add([{ id, quantity: 1 }], { source: 'quiz', origin: button });
        label.textContent = this.i18n.added;
        this.announce(this.i18n.added);
        try {
          const saved = JSON.parse(sessionStorage.getItem('uc_quiz_result') || 'null');
          if (saved) sessionStorage.setItem('uc_quiz_result', JSON.stringify({ ...saved, variant_id: Number(id), added: true }));
        } catch (e) {
          /* niets */
        }
        setTimeout(() => (label.textContent = label.dataset.default), 2400);
      } catch (error) {
        label.textContent = label.dataset.default;
        this.announce(this.i18n.error);
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    /* Theme-editor: geselecteerde vraag of resultaat direct tonen */
    editorSelect(event) {
      if (!this.contains(event.target)) return;
      const stepIndex = this.steps.indexOf(event.target.closest('[data-step]'));
      if (stepIndex > -1) {
        this.index = stepIndex;
        this.render(false);
        return;
      }
      const pick = event.target.closest('[data-pick]');
      if (pick) {
        const alt = this.picks.find((p) => p !== pick && p.dataset.handle);
        this.showResult(pick, alt, { kind: false, cadeau: false });
        this.index = this.total;
        this.render(false);
      }
    }
  }

  customElements.define('uc-quiz', UcQuiz);
})();
